import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  VolumeUp as VolumeUpIcon,
  Help as HelpIcon,
  SentimentSatisfied as SmileyIcon,
  SentimentDissatisfied as SadIcon
} from '@mui/icons-material';
import TagSelector from './TagSelector';

const knowledgeLevels = [
  { value: 1, label: "Don't Know", color: 'error', icon: <SadIcon /> },
  { value: 2, label: 'Learning', color: 'warning', icon: <HelpIcon /> },
  { value: 3, label: 'Know Well', color: 'success', icon: <SmileyIcon /> }
];

const WordDialog = ({
  open,
  onClose,
  word = null,
  onSave,
  languageCode,
  playPronunciation = null,
  disableAutoFocus = false
}) => {
  const [formData, setFormData] = useState({
    word: '',
    translation: '',
    context: '',
    knowledge_level: 1,
    tags: []
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize form when word changes or dialog opens
  useEffect(() => {
    if (word) {
      setFormData({
        word: word.word || '',
        translation: word.translation || '',
        context: word.context || '',
        knowledge_level: word.knowledge_level || 1,
        tags: word.tags || []
      });
    } else {
      // Reset for new word
      setFormData({
        word: '',
        translation: '',
        context: '',
        knowledge_level: 1,
        tags: []
      });
    }
    setErrors({});
  }, [word, open]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleTagsChange = (newTags) => {
    setFormData(prev => ({
      ...prev,
      tags: newTags
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.word.trim()) {
      newErrors.word = 'Word is required';
    }

    if (!formData.translation.trim()) {
      newErrors.translation = 'Translation is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      // Prepare data for saving
      const dataToSave = {
        ...formData,
        language_code: languageCode,
        // Extract just tag names for the API
        tags: formData.tags.map(tag =>
          typeof tag === 'string' ? tag : tag.tag_name
        )
      };

      // If word has an ID, include it for updates
      if (word?.id) {
        dataToSave.id = word.id;
      }

      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error saving word:', error);
      setErrors({ submit: 'Failed to save word. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePlayPronunciation = () => {
    if (playPronunciation && formData.word) {
      playPronunciation(formData.word, languageCode);
    }
  };

  const isEditMode = Boolean(word?.id);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableAutoFocus={disableAutoFocus}
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {isEditMode ? 'Edit Vocabulary Word' : 'Add to Vocabulary'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Word Input */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
              fullWidth
              label="Word"
              value={formData.word}
              onChange={(e) => handleFieldChange('word', e.target.value)}
              error={Boolean(errors.word)}
              helperText={errors.word}
              disabled={isEditMode} // Can't edit the word itself
              autoFocus={!disableAutoFocus && !isEditMode} // Only autoFocus if not disabled
              inputProps={{
                autoComplete: 'off'
              }}
            />
            {playPronunciation && (
              <Tooltip title="Play pronunciation">
                <IconButton
                  color="primary"
                  onClick={handlePlayPronunciation}
                  disabled={!formData.word}
                  sx={{ mt: 0.5 }}
                >
                  <VolumeUpIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Translation Input */}
          <TextField
            fullWidth
            label="Translation"
            value={formData.translation}
            onChange={(e) => handleFieldChange('translation', e.target.value)}
            error={Boolean(errors.translation)}
            helperText={errors.translation}
            inputProps={{
              autoComplete: 'off'
            }}
          />

          {/* Context Input */}
          <TextField
            fullWidth
            label="Context (Optional)"
            value={formData.context}
            onChange={(e) => handleFieldChange('context', e.target.value)}
            multiline
            rows={2}
            placeholder="Example sentence or note about usage..."
            inputProps={{
              autoComplete: 'off'
            }}
          />

          {/* Knowledge Level Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Knowledge Level
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {knowledgeLevels.map(level => (
                <Chip
                  key={level.value}
                  label={level.label}
                  icon={level.icon}
                  color={formData.knowledge_level === level.value ? level.color : 'default'}
                  variant={formData.knowledge_level === level.value ? 'filled' : 'outlined'}
                  onClick={() => handleFieldChange('knowledge_level', level.value)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Tags Selection */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Tags (Optional)
            </Typography>
            <TagSelector
              selectedTags={formData.tags}
              onTagsChange={handleTagsChange}
              languageCode={languageCode}
            />
          </Box>

          {/* Error Message */}
          {errors.submit && (
            <Typography color="error" variant="body2">
              {errors.submit}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WordDialog;